import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button } from '@caruso/ui';
import { executionsService } from '@/services/executions.service';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Image as ImageIcon,
  RefreshCw,
  TrendingUp,
  Activity as ActivityIcon,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ActivitiesDashboard() {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  const { data: executions, isLoading, refetch } = useQuery({
    queryKey: ['today-executions', selectedDate],
    queryFn: () => executionsService.getTodayExecutions(selectedDate),
  });

  // Calculate statistics
  const totalExecutions = executions?.length || 0;
  const completedExecutions =
    executions?.filter((e) => e.status === 'completed').length || 0;
  const inProgressExecutions =
    executions?.filter((e) => e.status === 'in_progress').length || 0;

  const completionRate =
    totalExecutions > 0
      ? Math.round((completedExecutions / totalExecutions) * 100)
      : 0;

  // Calculate average execution time (for completed ones)
  const completedWithTime = executions?.filter(
    (e) => e.status === 'completed' && e.startedAt && e.completedAt
  );
  const avgExecutionTime = completedWithTime?.length
    ? Math.round(
        completedWithTime.reduce((acc, e) => {
          const start = new Date(e.startedAt).getTime();
          const end = new Date(e.completedAt!).getTime();
          return acc + (end - start) / 1000 / 60; // minutes
        }, 0) / completedWithTime.length
      )
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">
            Dashboard de Atividades
          </h1>
          <p className="text-brand-ivory/60 mt-1">
            Visualize todas as execuções de processos do dia
          </p>
        </div>
        <div className="flex items-center gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-2 bg-brand-smoke/50 border border-brand-gold/20 rounded-lg text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
            style={{
              colorScheme: 'dark',
            }}
          />
          <Button onClick={() => refetch()} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-12 h-12 text-brand-gold animate-spin" />
        </div>
      ) : (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-brand-gold/20">
                  <ActivityIcon className="w-6 h-6 text-brand-gold" />
                </div>
                <div>
                  <p className="text-sm text-brand-ivory/60">Total</p>
                  <p className="text-2xl font-bold text-brand-gold">
                    {totalExecutions}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-brand-ink/50 border-green-500/20 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-500/20">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-brand-ivory/60">Concluídas</p>
                  <p className="text-2xl font-bold text-green-400">
                    {completedExecutions}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-brand-ink/50 border-blue-500/20 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-500/20">
                  <Clock className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-brand-ivory/60">Em Andamento</p>
                  <p className="text-2xl font-bold text-blue-400">
                    {inProgressExecutions}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-brand-ink/50 border-purple-500/20 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-500/20">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-brand-ivory/60">Taxa de Conclusão</p>
                  <p className="text-2xl font-bold text-purple-400">
                    {completionRate}%
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Executions List */}
          {executions && executions.length > 0 ? (
            <div className="space-y-4">
              <h2 className="text-xl font-serif text-brand-gold">
                Execuções do Dia
              </h2>

              {executions.map((execution, index) => {
                const completedTasks =
                  execution.tasks.filter((t) => t.status === 'completed').length || 0;
                const totalTasks = execution.tasks.length;
                const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

                return (
                  <motion.div
                    key={execution.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      className={`bg-brand-ink/50 p-6 ${
                        execution.status === 'completed'
                          ? 'border-green-500/30'
                          : 'border-brand-gold/20'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center ${
                              execution.status === 'completed'
                                ? 'bg-green-500/20'
                                : 'bg-brand-gold/20'
                            }`}
                          >
                            {execution.status === 'completed' ? (
                              <CheckCircle className="w-6 h-6 text-green-400" />
                            ) : (
                              <Clock className="w-6 h-6 text-brand-gold" />
                            )}
                          </div>
                        </div>

                        <div className="flex-1 space-y-3">
                          {/* Header */}
                          <div>
                            <h3 className="text-lg font-semibold text-brand-gold">
                              {execution.process.name}
                            </h3>
                            <p className="text-sm text-brand-ivory/60">
                              {execution.process.description}
                            </p>
                          </div>

                          {/* Employee Info */}
                          <div className="flex items-center gap-2 text-sm text-brand-ivory/80">
                            <User className="w-4 h-4" />
                            <span>
                              {execution.user?.name || 'Usuário desconhecido'}
                            </span>
                            {execution.user?.client && (
                              <span className="text-brand-ivory/60">
                                ({execution.user.client.name})
                              </span>
                            )}
                          </div>

                          {/* Progress Bar */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-brand-ivory/60">
                                Progresso: {completedTasks}/{totalTasks} tarefas
                              </span>
                              <span className="text-brand-gold font-medium">
                                {Math.round(progress)}%
                              </span>
                            </div>
                            <div className="h-2 bg-brand-smoke/50 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  execution.status === 'completed'
                                    ? 'bg-green-500'
                                    : 'bg-brand-gold'
                                }`}
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>

                          {/* Time Information */}
                          <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center gap-1.5 text-brand-ivory/60">
                              <Calendar className="w-4 h-4" />
                              <span>
                                Iniciado em:{' '}
                                {format(
                                  new Date(execution.startedAt),
                                  "dd/MM/yyyy 'às' HH:mm",
                                  { locale: ptBR }
                                )}
                              </span>
                            </div>

                            {execution.completedAt && (
                              <div className="flex items-center gap-1.5 text-green-400">
                                <CheckCircle className="w-4 h-4" />
                                <span>
                                  Concluído em:{' '}
                                  {format(
                                    new Date(execution.completedAt),
                                    "dd/MM/yyyy 'às' HH:mm",
                                    { locale: ptBR }
                                  )}
                                </span>
                              </div>
                            )}

                            {execution.completedAt && (
                              <div className="flex items-center gap-1.5 text-brand-gold">
                                <Clock className="w-4 h-4" />
                                <span>
                                  Tempo total:{' '}
                                  {Math.round(
                                    (new Date(execution.completedAt).getTime() -
                                      new Date(execution.startedAt).getTime()) /
                                      1000 /
                                      60
                                  )}{' '}
                                  min
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Tasks with Photos */}
                          {execution.tasks.some((t) => t.photoUrl) && (
                            <div className="pt-3 border-t border-brand-gold/10">
                              <p className="text-sm font-medium text-brand-ivory mb-2">
                                Fotos anexadas:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {execution.tasks
                                  .filter((t) => t.photoUrl)
                                  .map((task) => (
                                    <a
                                      key={task.id}
                                      href={task.photoUrl!}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm transition-colors border border-blue-500/30"
                                    >
                                      <ImageIcon className="w-4 h-4" />
                                      <span>{task.task.name}</span>
                                    </a>
                                  ))}
                              </div>
                            </div>
                          )}

                          {/* Notes */}
                          {execution.notes && (
                            <div className="text-sm text-brand-ivory/60 italic pt-2 border-t border-brand-gold/10">
                              <span className="font-medium text-brand-ivory">
                                Observações:
                              </span>{' '}
                              {execution.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <Card className="bg-brand-ink/50 border-brand-gold/20">
              <div className="p-12 text-center">
                <XCircle className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
                <h3 className="text-xl font-serif text-brand-gold mb-2">
                  Nenhuma execução encontrada
                </h3>
                <p className="text-brand-ivory/60">
                  Não há execuções de processos registradas para esta data.
                </p>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
